import { TrackingEvent } from '../types/tracking'
import { EscrowOrderDetailsClickTarget, EscrowOrderStatus } from '../constants/escrow-order'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget =
  | 'upload_item'
  | 'confirm_everything_is_ok'
  | 'i_have_an_issue'
  | 'reupload_item'
  | 'physical_auth_buyer'

type EscrowOrderDetailsViewEventArgs = {
  screen: string
  escrow_order_id: string
  user_side: string
  escrow_order_status: EscrowOrderStatus
  is_suspended: boolean
  actions: Array<string> | null
  details: string | null
}

export const escrowOrderDetailsViewEvent = (
  args: EscrowOrderDetailsViewEventArgs,
): TrackingEvent => {
  const {
    screen,
    escrow_order_id,
    user_side,
    escrow_order_status,
    is_suspended,
    actions,
    details,
  } = args

  return {
    event: 'escrow_order_details.view',
    extra: {
      screen,
      escrow_order_id,
      user_side,
      escrow_order_status,
      is_suspended,
      actions,
      details,
    },
  }
}

type EscrowOrderDetailsActionLatencyViewEventArgs = {
  screen: string
  escrow_order_id: string
  escrow_order_status?: EscrowOrderStatus
  actions: Array<string> | null
  latency_ms: string
  details: string | null
}

export const escrowOrderDetailsActionLatencyViewEvent = (
  args: EscrowOrderDetailsActionLatencyViewEventArgs,
): TrackingEvent => {
  const { screen, escrow_order_id, escrow_order_status, actions, latency_ms, details } = args

  return {
    event: 'escrow_order_details.action_latency.view',
    extra: {
      screen,
      escrow_order_id,
      escrow_order_status,
      actions,
      latency_ms,
      details,
    },
  }
}

type EscrowOrderDetailsClickEventArgs = {
  screen: string
  escrow_order_id: string
  user_side: string
  escrow_order_status?: EscrowOrderStatus
  is_suspended?: boolean
  target: EscrowOrderDetailsClickTarget
  details: string | null
}

export const escrowOrderDetailsClickEvent = (
  args: EscrowOrderDetailsClickEventArgs,
): TrackingEvent => {
  const { screen, escrow_order_id, user_side, escrow_order_status, is_suspended, target, details } =
    args

  return {
    event: 'escrow_order_details.click',
    extra: {
      screen,
      escrow_order_id,
      user_side,
      escrow_order_status,
      is_suspended,
      target,
      details,
    },
  }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type ViewScreenEventArgs = {
  screen: string
}

export const viewScreenEvent = (args: ViewScreenEventArgs): TrackingEvent => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type SelfServiceViewScreenEventArgs = {
  screen: string
  transactionId: number | string
  faqEntryId?: number | string | null
  details?: string | null
  variant?: string | null
}

type SelfServiceViewScreenEventExtra = {
  screen: string
  transaction_id: number | string
  faq_entry_id?: number | string | null
  details?: string | null
  // "variant" is not present in the schema, however it is used in some places
  variant?: string | null
}

export const selfServiceViewScreenEvent = (args: SelfServiceViewScreenEventArgs): TrackingEvent => {
  const { screen, transactionId, faqEntryId, details, variant } = args

  const extra: SelfServiceViewScreenEventExtra = {
    screen,
    transaction_id: transactionId,
  }

  if (faqEntryId) extra.faq_entry_id = faqEntryId
  if (details) extra.details = details
  if (variant) extra.variant = variant

  return {
    event: 'user.self_service.view_screen',
    extra,
  }
}
